<i18n lang='yml' src='../i18n/BookingUserSearch.yml'></i18n>
<template>
  <div v-if="active">
    <flash :message_type="message_type" :message_text="message_text"/>
    <Form ref="observer" v-slot="{ errors }" @submit="search" @invalid-submit="setErrorMessage">
        <fieldset class="form-group pt-4">
          <label for="user_search"> {{$t('guiding_text')}} </label>
          <div class="form-group row mb-4 mt-2">
            <div class="col-12 col-md-6 col-lg-5 col-xl-4">
              <label for="first_name"> {{$t('first_name')}} </label>
              <input @keyup.enter="search()" class="form-control" id="first_name" type="text" v-model="firstName"/>
            </div>
            <div class="col-12 col-md-6 col-lg-5 col-xl-4">
              <label for="last_name"> {{$t('last_name')}}</label>
              <input @keyup.enter="search()" class="form-control" id="last_name" type="text" v-model="lastName"/>
            </div>
          </div>
          <div class="form-group row">
            <div class="col-12 col-md-6 col-lg-5 col-xl-4">
              <label for="sto_id"> {{$t('sto_id')}} </label>
              <Field
                :name="$t('sto_id')"
                :rules="{regex: ['numeric8', $t('sto_id').toLowerCase(), true]}"
                 v-slot="{ errors, field }"
                 v-model="stoId">
                <input  :class="{'is-invalid': errors[0]}" @keyup.enter="search()" class="form-control" id="sto_id" type="text" v-bind="field" maxlength="8"/>
                <div class="invalid-feedback" :id="`${$t('other_information_field')}_error`" v-if="errors[0]">
                  <font-awesome-icon :icon="['far', 'exclamation-circle']" fixed-width />
                  <span>{{ errors[0] }}</span> 
                </div>
              </Field>
            </div>
            <div class="col-12 col-md-6 col-lg-5 col-xl-4">
              <label for="phone_number"> {{$t('phone_number')}} </label>
              <Field
                :name="$t('phone_number')"
                :rules="{regex: ['phone', $t('phone_number').toLowerCase(), true]}"
                v-slot="{ errors, field }"
                v-model="phoneNumber">
                <input  :class="{'is-invalid': errors[0]}" @keyup.enter="search()" class="form-control" id="phone_number" type="text" v-bind="field"/>
                <div class="invalid-feedback" :id="`${$t('other_information_field')}_error`" v-if="errors[0]">
                  <font-awesome-icon :icon="['far', 'exclamation-circle']" fixed-width />
                  <span>{{ errors[0] }}</span> 
                </div>
              </Field>
            </div>
          </div>
          <button :set="setErrorMessage" href="#" type="submit" :disabled="hideSearchButton()" class="btn btn-md btn-success mb-2">
            {{$t('search')}}
          </button>
        </fieldset>
    </Form>
    <div v-if="loading">
      <loading-animation-vue :spinnerSize="40"></loading-animation-vue>
    </div>
    <div v-else>
      <div v-if="hasSearched && totalUsers.length > 0">
        <p> {{$t('search_results_guiding_text')}} </p>
        <p> {{`${totalUsers.length} ${$t('clients_found')}`}} </p>
      </div>
      <div v-if="hasSearched && totalUsers.length == 0">
        <p> {{$t('no_results')}} </p>
      </div>
      <div v-if="!hasSearched">
        <p> {{$t('results_guiding_text')}} </p>
      </div>
    </div>
    <div v-for="user in users" :key="user.id" class="row">
      <a class="post-meta col-sm-12 col-lg-8" href="#" @click="setSelectedUser(user)">
        <ul class="list-inline list-annotation">
          <li class="w-100 user-name">
            {{`${user.first_name} ${user.last_name}`}}
          </li>
          <li class="list-inline-item">
            {{ `${$t('mtcu_id')} ${user.mtcu_party_id}`}}
          </li>
          <li class="list-inline-item">
            {{ `${$t('sto_id')} ${user.sto_id}`}}
          </li>
          <li class="list-inline-item">
            {{ `${$t('birth_date')} ${formatDate(user.birth_date)}`}}
          </li>
          <li class="list-inline-item">
            {{ `${$t('phone_number')} ${user.phone_number}`}}
          </li>
        </ul>
      </a>
    </div>
    <pagination @items_changed="setUsers" :items="totalUsers" :expansion_threshold="expansion_threshold" :pagination_limit="pagination_limit"/>
  </div>
</template>
<script>
import Pagination from '../../../shared/pagination/Pagination.vue';
import LoadingAnimation from '../../../shared/loader/components/LoadingAnimation.vue';
import flash from '../../../shared/flash_messages/components/Flash.vue'
import '../../../../javascripts/application/vee-validate-rules'
import { Form, Field } from 'vee-validate'
import { sendAjaxRequest } from '../../../shared/typescripts/ajax.ts'
import { setErrorMessage } from '../../../shared/typescripts/error.ts'
import { formatDate } from '../../../shared/typescripts/dates.ts'

export default {
  props: ['locale', 'base_path', 'active', 'amanda_integration', 'pagination_limit', 'expansion_threshold'],
  components: {
    'pagination': Pagination,
    'loading-animation': LoadingAnimation,
    flash,
    Form,
    Field
  },
  data() {
    return {
      users: [],
      totalUsers: [],
      selectedUser: null,
      eligibility: null,
      firstName: '',
      lastName: '',
      stoId: '',
      phoneNumber: '',
      loading: false,
      hasSearched: false,
      message_text: [],
      message_type: "",
      showErrorBanner: true
    }
  },
  methods: {
    setSelectedUser(user) {
      if(this.amanda_integration == 'true') {
        sendAjaxRequest('GET', '/integrations/amanda_user_details', 'json', {
          sto_id: user.sto_id, first_name: user.first_name, last_name: user.last_name, email: user.email},
          this.setUserSuccess)
      }
      else {
        this.selectedUser = user
        this.nextStep()
      }
    },
    setUserSuccess(data) {
      if(data != null) {
        this.selectedUser = data.user_identity;
        this.eligibility = data.eligibility
        this.nextStep()
      }
    },
    setUsers(users) {
      this.users = users
    },
    search() {
      // Three characters is long enough to limit search time so we aren't searching for single or double characters
      // which could produce thousands of results and take an exceptionally long time to runt
      this.loading = true
      let url = this.amanda_integration == 'true' ? '/integrations/amanda_users' : '/users.json'
      let data = this.amanda_integration == 'true' ? {
            first_name: this.firstName,
            last_name: this.lastName,
            phone_number: this.phoneNumber,
            sto_id: this.stoId
          } : { q: `${this.firstName} ${this.lastName}` }
      sendAjaxRequest('GET', url, 'text', data, this.searchSuccess);
    },
    searchSuccess(data) {
      this.totalUsers = JSON.parse(data);
      this.loading = false
      this.hasSearched = true;
    },
    formatDate(dateString) {
      return formatDate(dateString)
    },
    nextStep() {
      if(this.selectedUser) {
        this.$emit('userChange', { 
          'user_identity_id': this.selectedUser.id, 
          'user_name': this.amanda_integration == 'true' ? this.selectedUser.full_name : this.selectedUser.name, 
          'sto_id': this.selectedUser.external_identifiers ? this.selectedUser.external_identifiers.sto_id : ""
        })
        this.$emit('userEligibility', this.eligibility)
        this.$emit('nextStep')
      }
    },
    hideSearchButton() {
      return (!this.firstName && !this.lastName && !this.stoId && !this.phoneNumber)
    },

    setErrorMessage({ valuese, errors, results }) {
      setErrorMessage(this, errors, true)
    }
  }
}
</script>
